/**
 * 役職の統一定数（表示用 fallback）
 *
 * 役職(job title)の正本は member_role_masters（DB）。code は3桁数値文字列 "001"〜"008"。
 * このファイルは DB 取得失敗時の静的 fallback ラベル/カラー/選択肢を提供する。
 * 認証ロール(permission)の正本は lib/auth/roles.ts。
 *
 * 旧設計の英語スラッグ（representative_director 等）はファントムコードで、
 * roles-legacy.ts に @deprecated として隔離（後方互換のラベル/カラー変換のみ）。
 */
import { LEGACY_ROLE_MASTER_LABELS, LEGACY_ROLE_MASTER_COLORS } from './roles-legacy'

/** member_role_masters.code の union 型（DB 正本: Issue #4263）*/
export type RoleMasterCode = '001' | '002' | '003' | '004' | '005' | '006' | '007' | '008'

/**
 * 役職マスタ未紐付け（roleMasterCode が null）を表す明示的なセンチネル値。
 *
 * 旧実装は未紐付けの場合 role を 'staff' にデフォルト埋めし、さらに roleGroupCode で
 * '008'（スタッフ）に正規化していたため、本物の '008' ロールの人と役職未設定の人が
 * 画面上で区別できなかった（監査 ADM1-H03）。
 *
 * '001'〜'008' の DB コード・後方互換の英語スラッグ（'staff' 等）のいずれとも衝突しない値にし、
 * roleGroupCode がどのグループにも正規化しない（= その他/未設定として素通しする）ようにする。
 */
export const UNSET_ROLE_CODE = '__unset__'

/** UNSET_ROLE_CODE の表示ラベル。「未設定」であることを明示し「その他」とは区別する。 */
export const UNSET_ROLE_LABEL = '未設定'

/**
 * member_role_masters.code → 日本語ラベル（実DB: 001-008）。
 * 英語スラッグは後方互換で残置（roles-legacy）。
 */
export const ROLE_MASTER_LABELS: Record<string, string> = {
  '001': '社長',
  '002': '役員',
  '003': '事業部長',
  '004': '事業部長サブ',
  '005': '部長',
  '006': '課長',
  '007': 'リーダー',
  '008': 'スタッフ',
  ...LEGACY_ROLE_MASTER_LABELS,
}

/**
 * 統合ラベルマップ。
 * 数値コード（001-008）を主とし、認証ロール・旧コード・英語スラッグを後方互換で残置。
 * 既存の表示を変えないため、認証ロール/旧コードの値は従来どおり維持する。
 */
export const ROLE_LABELS: Record<string, string> = {
  // 役職コード（member_role_masters.code・正本はDB・これは静的fallback）
  '001': '社長',
  '002': '役員',
  '003': '事業部長',
  '004': '事業部長サブ',
  '005': '部長',
  '006': '課長',
  '007': 'リーダー',
  '008': 'スタッフ',
  // 英語スラッグ（@deprecated・後方互換）
  ...LEGACY_ROLE_MASTER_LABELS,
  // 認証ロール・旧コード（後方互換: 既存の表示値を維持）
  president: '社長',
  chairman: '会長',
  admin: '管理者',
  manager: '部長',
  sales_manager: '営業部長',
  sales_leader: '営業リーダー',
  design_manager: '部門長',
  construction_manager: '部門長',
  supervisor: '現場監督',
  reception: '受付事務',
  hr: '人事・労務',
  marketing: 'マーケティング',
  after_support: 'アフターサポート',
}

/**
 * システム権限（users.role の admin / manager / staff）の表示語。
 *
 * 組織・ユーザー管理の「システム権限」Select（users-panel.tsx）とプロフィール
 * （lib/components/legacy/profile-modal.tsx）の両方がここを引く（表示ソースは単一・2026-09-06）。
 * ROLE_LABELS は役職コード・英語スラッグ・認証ロールが混在した後方互換マップで、
 * 'staff' の語は roles-legacy の spread 由来のため、権限表示の根拠としては使わない
 * （lib/auth/roles.ts にも別値の ROLE_LABELS があり取り違えやすい・Codex SO 2026-09-06）。
 * ROLE_LABELS の同キーと同値であることは lib/constants/__tests__/roles.test.ts で固定する
 * （他画面が ROLE_LABELS 経由で出す語と食い違わないため。変えるときは両方を更新する）。
 * manager の語「部長」は Select の従来表示を継続する判断（2026-09-06）。「システム権限」に
 * 役職語を出さない思想とは緊張があるので、業務側が別の語を望むならここと ROLE_LABELS.manager を同時に変える。
 */
export const SYSTEM_ROLE_LABELS: Record<'admin' | 'manager' | 'staff', string> = {
  admin: '管理者',
  manager: '部長',
  staff: 'スタッフ',
}

/**
 * member_role_masters.level → カラークラス（DB level 駆動）。
 * DB に新役職を追加してもコード変更不要にするため、level からカラーを導出する。
 * level: 0=社長 / 2=役員 / 3=事業部長系 / 5=部長 / 6=課長 / 7=リーダー / 8=スタッフ
 */
export function getRoleColorClassByLevel(level: number): { bg: string; text: string } {
  if (level <= 1) return { bg: 'bg-red-200', text: 'text-red-800' } // 社長・役員上位
  if (level <= 4) return { bg: 'bg-amber-100', text: 'text-amber-800' } // 事業部長系
  if (level <= 7) return { bg: 'bg-yellow-100', text: 'text-yellow-700' } // 部長・課長・リーダー
  return { bg: 'bg-muted', text: 'text-label' } // スタッフ
}

/**
 * 役職カラー。数値コードは level 駆動で導出、認証ロール/英語スラッグは後方互換で残置。
 */
export const ROLE_COLORS: Record<string, { bg: string; text: string }> = {
  // 役職コード（level 駆動・実DB level 値）
  '001': getRoleColorClassByLevel(0),
  '002': getRoleColorClassByLevel(2),
  '003': getRoleColorClassByLevel(3),
  '004': getRoleColorClassByLevel(3),
  '005': getRoleColorClassByLevel(5),
  '006': getRoleColorClassByLevel(6),
  '007': getRoleColorClassByLevel(7),
  '008': getRoleColorClassByLevel(8),
  // 英語スラッグ（@deprecated・後方互換）
  ...LEGACY_ROLE_MASTER_COLORS,
  // 認証ロール・旧コード（後方互換）
  president: { bg: 'bg-red-200', text: 'text-red-800' },
  chairman: { bg: 'bg-red-200', text: 'text-red-800' },
  manager: { bg: 'bg-amber-100', text: 'text-amber-700' },
  sales_leader: { bg: 'bg-green-100', text: 'text-green-700' },
  sales_manager: { bg: 'bg-amber-100', text: 'text-amber-700' },
  admin: { bg: 'bg-purple-100', text: 'text-purple-700' },
  reception: { bg: 'bg-pink-100', text: 'text-pink-700' },
  hr: { bg: 'bg-violet-100', text: 'text-violet-700' },
  marketing: { bg: 'bg-fuchsia-100', text: 'text-fuchsia-700' },
  after_support: { bg: 'bg-sky-100', text: 'text-sky-700' },
  supervisor: { bg: 'bg-lime-100', text: 'text-lime-700' },
}

/** ROLE_COLORS から cn() で使える単一文字列を返すヘルパー */
export function getRoleColorClass(role: string): string {
  const c = ROLE_COLORS[role]
  return c ? `${c.bg} ${c.text}` : 'bg-muted text-label'
}

/**
 * 役職表示グループ定義（member_role_masters.code・DB取得失敗時の静的 fallback）。
 *
 * 正本は member_role_masters（DB）。UI は activeRoleMasters があればそちらを優先し、
 * 取得失敗時のみこの数値コード fallback を使う。
 *
 * リネーム履歴: 旧 `ROLE_GROUPS` → 新 `ROLE_DISPLAY_GROUPS` (2026-06-09・Issue #3824)
 * 理由: `lib/auth/roles.ts` の認可用 `ROLE_GROUPS` と同名別概念だったため混同防止。
 */
export const ROLE_DISPLAY_GROUPS: { label: string; roles: string[] }[] = [
  {
    label: '役職',
    roles: ['001', '002', '003', '004', '005', '006', '007', '008'],
  },
]
